// eslint-disable-next-line import/no-extraneous-dependencies
import { parse } from 'espree';
import { Position, MarkerType } from '@xyflow/react';

// this helper function returns the intersection point
// of the line between the center of the intersectionNode and the target node
function getNodeIntersection(intersectionNode, targetNode) {
  // https://math.stackexchange.com/questions/1724792/an-algorithm-for-finding-the-intersection-point-between-a-center-of-vision-and-a
  const { width: intersectionNodeWidth, height: intersectionNodeHeight } = intersectionNode.measured;
  const intersectionNodePosition = intersectionNode.internals.positionAbsolute;
  const targetPosition = targetNode.internals.positionAbsolute;

  const w = intersectionNodeWidth / 2;
  const h = intersectionNodeHeight / 2;

  const x2 = intersectionNodePosition.x + w;
  const y2 = intersectionNodePosition.y + h;
  const x1 = targetPosition.x + targetNode.measured.width / 2;
  const y1 = targetPosition.y + targetNode.measured.height / 2;

  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1) || 1);
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  const x = w * (xx3 + yy3) + x2;
  const y = h * (-xx3 + yy3) + y2;

  return { x, y };
}

// returns the position (top,right,bottom or right) passed node compared to the intersection point
function getEdgePosition(node, intersectionPoint) {
  const n = { ...node.internals.positionAbsolute, ...node };
  const nx = Math.round(n.x);
  const ny = Math.round(n.y);
  const px = Math.round(intersectionPoint.x);
  const py = Math.round(intersectionPoint.y);

  if (px <= nx + 1) {
    return Position.Left;
  }
  if (px >= nx + n.measured.width - 1) {
    return Position.Right;
  }
  if (py <= ny + 1) {
    return Position.Top;
  }
  if (py >= n.y + n.measured.height - 1) {
    return Position.Bottom;
  }

  return Position.Top;
}

// returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) you need to create an edge
export function getEdgeParams(source, target) {
  const sourceIntersectionPoint = getNodeIntersection(source, target);
  const targetIntersectionPoint = getNodeIntersection(target, source);

  const sourcePos = getEdgePosition(source, sourceIntersectionPoint);
  const targetPos = getEdgePosition(target, targetIntersectionPoint);

  return {
    sx: sourceIntersectionPoint.x,
    sy: sourceIntersectionPoint.y,
    tx: targetIntersectionPoint.x,
    ty: targetIntersectionPoint.y,
    sourcePos,
    targetPos
  };
}

export function createNodesAndEdges() {
  const nodes = [];
  const edges = [];
  const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };

  nodes.push({ id: 'target', data: { label: 'Target' }, position: center });

  for (let i = 0; i < 8; i += 1) {
    const degrees = i * (360 / 8);
    const radians = degrees * (Math.PI / 180);
    const x = 250 * Math.cos(radians) + center.x;
    const y = 250 * Math.sin(radians) + center.y;

    nodes.push({ id: `${i}`, data: { label: 'Source' }, position: { x, y } });

    edges.push({
      id: `edge-${i}`,
      target: 'target',
      source: `${i}`,
      type: 'floating',
      markerEnd: {
        type: MarkerType.Arrow
      }
    });
  }

  return { nodes, edges };
}

// Transform a single node to backend format
export const transformNode = (node) => {
  const groupNodes = {};
  if (node?.parentId) groupNodes.parentId = node?.parentId;
  if (node?.extent) groupNodes.extent = node?.extent;
  if (node?.positionAbsolute) groupNodes.positionAbsolute = node?.positionAbsolute;
  if (node?.style) groupNodes.style = node?.style;
  if (node?.width) groupNodes.width = node?.width;
  if (node?.height) groupNodes.height = node?.height;
  // Base node structure
  const baseNode = {
    id: node.id,
    type: 'default',
    customType: node.type,
    data: {
      label: node.data.prompt || node.data.tool_name || node.id,
      description: node.data.description || `Node ${node.id}`
    },
    ...groupNodes,
    position: node.position
  };

  // Add specific configurations based on node type
  if (node.type === 'agent') {
    baseNode.data.intentType = node.data?.intentType;
    baseNode.data.agentConfig = {
      model: node.data.config?.model?.model || 'gpt-4o',
      provider: node.data.config?.model?.provider || 'open-ai',
      prompt:
        node.data.config?.systemPrompt ||
        'You are a super helpful teacher that explains student questions step by step. You should explain for grade 5 students. Always try to cheer the student up and make them feel good.',
      temperature: node.data.config?.model?.temperature ?? 0.7,
      maxTokens: node.data.config?.model?.maxTokens || 200,
      promptType: node.data.config?.model?.promptType || '',
      additionalMetadata: node.data.config?.additionalMetadata || {},
      otherParameters: node.data.config?.otherParameters || {},
      // Include GPT-5 specific config directly in agentConfig (support both old and new structure)
      ...(node.data.config?.agentConfig?.gpt5Config && {
        reasoningEffort: node.data.config.agentConfig.gpt5Config.reasoningEffort,
        verbosity: node.data.config.agentConfig.gpt5Config.verbosity
      }),
      // Also include if they're already stored directly in agentConfig
      ...(node.data.config?.agentConfig?.reasoningEffort && {
        reasoningEffort: node.data.config.agentConfig.reasoningEffort
      }),
      ...(node.data.config?.agentConfig?.verbosity && {
        verbosity: node.data.config.agentConfig.verbosity
      })
    };
    baseNode.data.parsers = node.data.config?.parsers || [];
  } else if (node.type === 'supervisor') {
    baseNode.data.agentConfig = {
      model: node.data.config?.model?.model || 'gpt-4o',
      prompt:
        node.data.config?.systemPrompt ||
        // eslint-disable-next-line max-len
        "You are a routing and output agent. Given the user's last message, carefully analyse and execute only those agents and give the output as is from the agents and in a clear and separated manner and do not mix the outputs from different agents. Also do not generate anything from your end STRICTLY. In the end just print the name of each agent that were used in individual brackets.",
      temperature: node.data.config?.model?.temperature ?? 0.7,
      maxTokens: node.data.config?.model?.maxTokens || 200,
      promptType: node.data.config?.model?.promptType || '',
      otherParameters: node.data.config?.otherParameters || {},
      // Include GPT-5 specific config directly in agentConfig (support both old and new structure)
      ...(node.data.config?.agentConfig?.gpt5Config && {
        reasoningEffort: node.data.config.agentConfig.gpt5Config.reasoningEffort,
        verbosity: node.data.config.agentConfig.gpt5Config.verbosity
      }),
      // Also include if they're already stored directly in agentConfig
      ...(node.data.config?.agentConfig?.reasoningEffort && {
        reasoningEffort: node.data.config.agentConfig.reasoningEffort
      }),
      ...(node.data.config?.agentConfig?.verbosity && {
        verbosity: node.data.config.agentConfig.verbosity
      })
    };
    // Add agentConfigs array to store connected agent configurations
    baseNode.data.agentConfigs = node.data.agentConfigs || [];
  } else if (node.type === 'tool') {
    const responseFormat = node.data.config?.responseFormat;
    if (node.data.toolId) {
      baseNode.data.toolId = node.data.toolId;
    }

    // Check if this is an MCPServer tool by checking if mcpConfig exists
    const isMCPServer = !!node.data.toolConfig?.mcpConfig;

    baseNode.data.toolConfig = {
      jsMethod: node.data.config?.implementation?.name || node.data.tool_name,
      jsMethodCode:
        node.data.config?.implementation?.code || `function ${node.data.tool_name}(params) { return params; }`,
      zodSchema: JSON.stringify(node.data.config?.parameters || { type: 'object', properties: {} }),
      ...(responseFormat && { responseFormat }),
      // Conditional configuration based on tool type
      ...(isMCPServer
        ? {
            // MCPServer configuration - preserve existing mcpConfig as JSON object
            mcpConfig:
              typeof node.data.toolConfig?.mcpConfig === 'string'
                ? JSON.parse(node.data.toolConfig?.mcpConfig || '{}')
                : node.data.toolConfig?.mcpConfig || {}
          }
        : {
            // RestAPI configuration (default) - preserve existing apiConfig
            apiConfig: node.data.toolConfig?.apiConfig || {
              endpoint: node.data.config?.implementation?.endpoint || '',
              method: node.data.config?.implementation?.method || 'POST',
              authType: node.data.config?.implementation?.authType || 'None',
              headers: {},
              payload: {}
            }
          }),
      advancedOptions: node.data.toolConfig?.advancedOptions ||
        node.data.config?.advancedOptions || {
          timeout: 3000,
          retry: 1
        }
    };
  }
  return baseNode;
};

// Transform a single edge to backend format
export const transformEdge = (edge) => {
  const baseEdge = {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    label: edge.data?.label || 'continue',
    sourceHandle: edge.sourceHandle || '',
    targetHandle: edge.targetHandle || ''
  };
  if (edge.data?.conditional) {
    baseEdge.condition = {
      expression: edge.data?.conditionExpression || 'true',
      evaluationType: 'javascript'
    };
  }
  return baseEdge;
};

export const validateJavaScript = (code) => {
  const result = {
    isValid: false,
    errors: [],
    warnings: []
  };

  // Step 1: Empty check
  if (!code || !code.trim()) {
    result.errors.push('JavaScript code cannot be empty');
    return result;
  }

  const trimmedCode = code.trim();

  // Step 2: Syntax check using Espree
  try {
    // Wrap code in parentheses if it’s an arrow function to make it parsable
    const isArrowFunction = /^\s*(async\s*)?\([^)]*\)\s*=>/.test(trimmedCode);
    const parseCode = isArrowFunction ? `(${trimmedCode})` : trimmedCode;

    parse(parseCode, {
      ecmaVersion: 'latest',
      sourceType: 'script',
      ecmaFeatures: {
        globalReturn: true
      }
    });
  } catch (err) {
    result.errors.push(`Syntax error: ${err.message}`);
    return result;
  }

  // Step 3: Function structure validation
  const functionPatterns = [
    /^\s*(async\s+)?function\s+\w+\s*\([^)]*\)\s*\{[\s\S]*\}\s*$/, // function declaration
    /^\s*(?:const|let|var)\s+\w+\s*=\s*(async\s+)?\([^)]*\)\s*=>\s*\{[\s\S]*\}\s*$/, // assigned arrow function
    /^\s*\([^)]*\)\s*=>\s*\{[\s\S]*\}\s*$/, // bare arrow function
    /^\s*async\s*\([^)]*\)\s*=>\s*\{[\s\S]*\}\s*$/ // async arrow function
  ];

  const isValidFunction = functionPatterns.some((pattern) => pattern.test(trimmedCode));

  if (!isValidFunction) {
    result.errors.push('Code must be a valid JavaScript function (declaration, expression, or arrow function)');
    return result;
  }

  // Step 4: Dangerous pattern detection
  const dangerousPatterns = [
    { pattern: /eval\s*\(/, message: 'eval() is not allowed for security reasons' },
    { pattern: /Function\s*\(/, message: 'Function constructor is not allowed for security reasons' },
    { pattern: /setTimeout\s*\(/, message: 'setTimeout() should be avoided in tool implementations' },
    { pattern: /setInterval\s*\(/, message: 'setInterval() should be avoided in tool implementations' },
    { pattern: /document\./, message: 'DOM manipulation is not allowed in tool implementations' },
    { pattern: /window\./, message: 'Window object access is not allowed in tool implementations' },
    { pattern: /global\./, message: 'Global object access should be avoided' },
    { pattern: /process\./, message: 'Process object access should be avoided' },
    { pattern: /require\s*\(/, message: 'require() is not allowed, use import statements instead' },
    {
      pattern: /import\s+.*\s+from\s+['"][^'"]+['"]/,
      message: 'Dynamic imports should be avoided'
    }
  ];

  // eslint-disable-next-line no-restricted-syntax
  for (const { pattern, message } of dangerousPatterns) {
    if (pattern.test(trimmedCode)) {
      result.warnings.push(message);
    }
  }

  // Step 5: Return statement check
  if (!/\breturn\b/.test(trimmedCode)) {
    result.warnings.push('Function should have a return statement');
  }

  // Step 6: Async/await mismatch
  if (/\bawait\b/.test(trimmedCode) && !/\basync\b/.test(trimmedCode)) {
    result.errors.push('Functions using await must be declared as async');
    return result;
  }

  // Step 7: Parameter structure check
  const parameterRegex = /\(\s*(\{[^}]*\}|\w+(?:\s*,\s*\w+)*)?\s*\)/;
  if (!parameterRegex.test(trimmedCode)) {
    result.warnings.push('Function should properly define parameters');
  }

  // Step 8: Unmatched braces and parentheses (fallback)
  const pairs = [
    { open: '{', close: '}', label: 'braces' },
    { open: '(', close: ')', label: 'parentheses' }
  ];
  // eslint-disable-next-line no-restricted-syntax
  for (const { open, close, label } of pairs) {
    const openCount = (trimmedCode.match(new RegExp(`\\${open}`, 'g')) || []).length;
    const closeCount = (trimmedCode.match(new RegExp(`\\${close}`, 'g')) || []).length;
    if (openCount !== closeCount) {
      result.errors.push(`Unmatched ${label} in code`);
      return result;
    }
  }

  // Step 9: Unreachable code after return
  const returnMatch = trimmedCode.match(/\breturn\b[\s\S]*?;/);
  if (returnMatch) {
    const afterReturn = trimmedCode.split(returnMatch[0])[1]?.trim();
    if (afterReturn && !afterReturn.startsWith('//') && !afterReturn.startsWith('}')) {
      result.errors.push('Code contains stray or unreachable content after return');
      return result;
    }
  }

  // If all checks passed
  result.isValid = true;
  return result;
};

export const generateReadableDataFlow = (logData) => {
  // Handle null or undefined logData
  if (!logData || !logData.executionLog) {
    return 'No execution logs available to display.';
  }

  const logs = logData.executionLog;
  const nodes = {};
  const links = [];

  const extractText = (messages) => {
    if (!messages?.length) return '';
    const raw = messages[0].content || messages[0]?.lc_kwargs?.content || '';
    try {
      const parsed = JSON.parse(raw);
      return parsed?.input || raw;
    } catch {
      return raw;
    }
  };

  const friendlyLabel = (name) => {
    if (name === '__start__') return 'Start Node';
    if (name.startsWith('ChannelWrite<branch:to:supervisor')) return 'Send to Supervisor';
    if (name.startsWith('ChannelWrite<branch:to:agent')) return 'Send to Agent';
    if (name.startsWith('ChannelWrite')) return 'Routing Message';
    if (name.startsWith('RunnableSequence')) return 'Internal Agent Chain';
    if (name.startsWith('prompt')) return 'Routing Prompt';
    if (name.startsWith('Branch<')) return 'Branch Decision';
    if (name === 'tools') return 'Tool Group Execution';
    if (name === 'agent') return 'Agent Router';
    if (name === 'supervisor') return 'Supervisor Node';
    if (name === 'LangGraph') return 'Workflow Start';
    // if (name.includes('Explain_Agent')) return 'Explain Agent';
    // if (name.includes('GetRAGTool')) return 'Retrieve Book Content Tool';
    // if (name.includes('GetImageDescription')) return 'Image Description Tool';
    // if (name.includes('GetPriorSessionChatTool')) return 'Previous Chat Context Tool';
    // if (name.includes('transfer_to_explain_agent')) return 'Transfer to Explain Agent Logic';
    return name; // fallback
  };

  // Collect node info
  logs.forEach((log) => {
    const { name, type, data } = log;
    if (!nodes[name]) nodes[name] = {};
    if (type === 'on_chain_start') nodes[name].input = extractText(data.input?.messages);
    if (type === 'on_chain_end') nodes[name].output = extractText(data.output?.messages || data.output);
  });

  // Order nodes based on appearance in logs
  const ordered = Object.entries(nodes).map(([name, val], idx) => ({
    id: idx + 1,
    rawName: name,
    label: friendlyLabel(name),
    input: val.input || '',
    output: val.output || ''
  }));

  // Link nodes based on output → input flow match
  for (let i = 0; i < ordered.length; i += 1) {
    const currentOutput = ordered[i].output?.trim();
    for (let j = i + 1; j < ordered.length; j += 1) {
      const nextInput = ordered[j].input?.trim();
      if (currentOutput && nextInput && currentOutput === nextInput) {
        links.push({ from: ordered[i].label, to: ordered[j].label });
        break;
      }
    }
  }

  // Print readable trace
  const res = ordered
    .map((node) => {
      const link = links.find((l) => l.from === node.label);
      const arrow = link ? ` → ${link.to}` : '';
      return `🔹 ${node.label}${arrow}\n   👉 Input: ${node.input}\n   🏁 Output: ${node.output}\n`;
    })
    .join('\n');

  return res;
};
